import {
  CamelCaseCondition,
  Condition,
  ConditionGroup,
} from '@admin/questionnaire/models';
import _, { cloneDeep, flatten } from 'lodash';
import {
  IAnswer,
  IModule,
  IQuestion,
  Questionnaire,
  QuestionType,
  SMEQC,
  SubModule,
  ConditionalOperator,
} from './models/questionnaire.model';
import { UtilsService } from '@shared/services/utils.service';
import { Injector, runInInjectionContext } from '@angular/core';
import { DatePipe } from '@angular/common';
import { WcgMenuOption } from '@wcg/frontend-framework';
import { SectionAssignmentContact } from './models';

export interface QuestionnaireState {
  activeQuestionnaireId?: string;
  questionnaire?: Questionnaire;
  questionnaireState: DerivedQuestionnaireState;
  section?: IModule;
  questionDict?: { [key: string]: { title: string; type: string; data?: any } };
  loadingQuestionnaire: {
    [key: string]: boolean;
  };
  savingModuleMarks: {
    [key: string]: {
      SME: boolean;
      QC: boolean;
    };
  };
  savingAnswerMarks: {
    [key: string]: {
      SME: boolean;
      QC: boolean;
    };
  };
  answerMarkErrors: {
    [key: string]: {
      SME: string;
      QC: string;
    };
  };
  moduleMarkErrors: {
    [key: string]: {
      SME: string;
      QC: string;
    };
  };
  loadingSubmissionTeam: {
    [key: string]: boolean;
  };
  submissionTeam: { [key: string]: SectionAssignmentContact[] };
}

export interface DerivedModule {
  id: string;
  title: string;
  questions: DerivedQuestion[];
  conditionGroups: ConditionGroup[];
  visible: boolean;
  visible_questions: number;
  total_questions: number;
  answered_questions: number;
  is_complete: boolean;
  parentId?: string;
  qcMark: boolean;
  smeMark: boolean;
}

export interface DerivedQuestion {
  id: string;
  title?: string;
  answerId?: string;
  answer: string | string[];
  visible: boolean;
  conditionGroups: ConditionGroup[];
  children?: DerivedQuestion[];
  moduleId: string;
  type: QuestionType;
  qcMark: boolean;
  smeMark: boolean;
  compositeKey?: string;
  // Add a flag to skip the question when counting answered questions, total questions, etc. and checking SME/QC marks
  skipForCount?: boolean;
}

export interface DerivedQuestionnaireState {
  submissionId?: string;
  questions: {
    [compositeKey: string]: DerivedQuestion; // compositeKey = questionId + (orderNum + answerId if duplicate)
  };
  modules: {
    [moduleId: string]: DerivedModule;
  };
}

/**
 * Derive the state of the questionnaire based on the answers to the questions
 * @param questionnaire Questionnaire object
 * @returns DerivedQuestionnaireState object
 */
export function deriveQuestionnaireState(
  questionnaire: Questionnaire,
): DerivedQuestionnaireState {
  // Sort modules, submodules, and questions by order number
  const modules = cloneDeep(questionnaire.modules)
    .sort((a, b) => a.orderNum - b.orderNum)
    .map((module) => {
      module.subModules = module.subModules
        ?.sort((a, b) => a.orderNum - b.orderNum)
        .map((subModule) => {
          subModule.questions = cloneDeep(subModule.questions ?? [])
            .map((question) => {
              question.children = question.children?.sort(
                (a, b) => a.orderNum - b.orderNum,
              );

              question.duplicates = question.duplicates?.map((duplicate) => {
                duplicate.children = duplicate.children?.sort(
                  (a, b) => a.orderNum - b.orderNum,
                );

                return duplicate;
              });

              return question;
            })
            .sort((a, b) => a.orderNum - b.orderNum);

          return subModule;
        });

      return module;
    });

  const questionnaireState: DerivedQuestionnaireState = {
    submissionId: questionnaire.submissionId,
    questions: {},
    modules: {},
  };

  // Do DFS to derive visibility of questions, submodules, and modules
  modules.forEach((module) => {
    // Iterate over each submodule in the module
    module.subModules?.forEach((subModule) => {
      // First derive visibility of questions
      // TODO: Refactor to create a function that can be used for both questions and their children
      subModule.questions.forEach((question) => {
        const derivedChildren = question.children?.map((child) => {
          const conditionGroups = createConditionGroups(child).concat(
            createConditionGroups(question),
          );

          const compositeKey = createCompositeKey(child);

          const questionAnswer: DerivedQuestion = {
            id: child.id,
            compositeKey,
            title: child.title,
            answerId: child.answer?.id,
            answer: child.answer?.value ?? '',
            conditionGroups,
            moduleId: subModule.id,
            type: child.type.name,
            visible: evaluateConditionGroups(
              conditionGroups,
              questionnaireState,
            ),
            qcMark: !!child.answer?.qcMark,
            smeMark: !!child.answer?.smeMark,
            skipForCount:
              question.type.name === 'group' ||
              question.type.name === 'document' ||
              child.type.name === 'document', //skip child question count when question type is document
          };

          child.compositeKey = questionAnswer.compositeKey;

          if (child.answer?.choices?.length > 0) {
            questionAnswer.answer = child.answer.choices;
          }

          if (child.answer?.choiceIds?.length > 0) {
            questionAnswer.answer = child.answer.choiceIds;
          }

          questionnaireState.questions[compositeKey] = questionAnswer;

          return questionAnswer;
        });

        question.duplicates?.forEach((duplicate) => {
          const derivedChildren = duplicate.children?.map((child) => {
            const conditionGroups = createConditionGroups(child);

            const compositeKey = createCompositeKey(child);

            const questionAnswer: DerivedQuestion = {
              id: child.id,
              compositeKey,
              title: child.title,
              answerId: child.answer?.id,
              answer: child.answer?.value ?? '',
              conditionGroups,
              moduleId: subModule.id,
              type: child.type.name,
              visible: evaluateConditionGroups(
                conditionGroups,
                questionnaireState,
              ),
              qcMark: !!child.answer?.qcMark,
              smeMark: !!child.answer?.smeMark,
              skipForCount:
                question.type.name === 'document' ||
                child.type.name === 'document', //skip duplicate child question count when question type is document
            };

            child.compositeKey = questionAnswer.compositeKey;

            if (child.answer?.choices?.length > 0) {
              questionAnswer.answer = child.answer.choices;
            }

            if (child.answer?.choiceIds?.length > 0) {
              questionAnswer.answer = child.answer.choiceIds;
            }

            questionnaireState.questions[compositeKey] = questionAnswer;

            return questionAnswer;
          });

          const compositeKey = createCompositeKey(duplicate);

          const questionAnswer: DerivedQuestion = {
            id: duplicate.id,
            compositeKey,
            title: duplicate.title,
            answerId: duplicate.answer?.id,
            answer: duplicate.answer?.value ?? '',
            conditionGroups: createConditionGroups(duplicate),
            moduleId: subModule.id,
            type: duplicate.type.name,
            visible: evaluateConditionGroups(
              createConditionGroups(duplicate),
              questionnaireState,
            ),
            qcMark: !!duplicate.answer?.qcMark,
            smeMark: !!duplicate.answer?.smeMark,
            children: derivedChildren,
          };

          duplicate.compositeKey = questionAnswer.compositeKey;

          questionnaireState.questions[compositeKey] = questionAnswer;
        });

        const conditionGroups = createConditionGroups(question);

        const compositeKey = createCompositeKey(question);

        const questionAnswer: DerivedQuestion = {
          id: question.id,
          compositeKey,
          title: question.title,
          answerId: question.answer?.id,
          answer: question.answer?.value ?? '',
          conditionGroups,
          moduleId: subModule.id,
          type: question.type.name,
          visible: evaluateConditionGroups(conditionGroups, questionnaireState),
          qcMark: !!question.answer?.qcMark,
          smeMark: !!question.answer?.smeMark,
          children: derivedChildren,
          skipForCount: question.type.name === 'document',
        };

        question.compositeKey = questionAnswer.compositeKey;

        if (question.answer?.choices?.length > 0) {
          questionAnswer.answer = question.answer.choices;
        }

        if (question.answer?.choiceIds?.length > 0) {
          questionAnswer.answer = question.answer.choiceIds;
        }

        questionnaireState.questions[compositeKey] = questionAnswer;
      });

      // Then derive visibility of the submodule
      const conditionGroups = createConditionGroups(subModule);

      const visible = evaluateConditionGroups(
        conditionGroups,
        questionnaireState,
      );

      const allQuestions = getSubmoduleQuestions(subModule);

      const visibleQuestions = allQuestions.filter((question) => {
        const questionState =
          questionnaireState.questions[question.compositeKey ?? question.id];

        // Group is not included here because it gets the QC/SME marks and the entire group is counted as one question
        // Whereas add-more and parent type questions are not counted as they are not visible and their children are independent questions
        return (
          !question.type.data?.isMessage &&
          questionState.visible &&
          ![
            'add-more',
            'parent',
            'acknowledgement',
            'text_message',
            'warning',
          ].includes(question.type.name)
        );
      });

      // Only get questions who should be counted (eg exclude group children)
      const countedQuestions = visibleQuestions.filter(
        (question) =>
          !questionnaireState.questions[question.compositeKey ?? question.id]
            .skipForCount,
      );

      if (!visible) {
        // If the submodule is not visible, set all questions to not visible
        allQuestions.forEach((question) => {
          const questionState =
            questionnaireState.questions[question.compositeKey ?? question.id];

          questionnaireState.questions[question.compositeKey ?? question.id] = {
            ...questionState,
            visible: false,
          };
        });
      }

      const formattedSubmodule: DerivedModule = {
        id: subModule.id,
        title: subModule.title,
        parentId: module.id,
        conditionGroups,
        visible,
        // The questions are "visible" but they are set to not visible if the submodule is not visible; keeping this for debugging purposes
        visible_questions: countedQuestions.length,
        total_questions: allQuestions.length,
        is_complete: false,
        answered_questions: countedQuestions.filter((question) => {
          // Perform the group-child validations and check if all children are answered
          // This can likely be refactored out of the code by adding some explicit logic for validations and child visibility
          // In an earlier stage of this process
          if (question.type.name === 'group') {
            const visibleChildren = question.children?.filter((child) => {
              const state =
                questionnaireState.questions[child.compositeKey ?? child.id];

              return state.visible;
            });

            const hasRequiredChildren = visibleChildren?.some((child) => {
              return child.data?.validation?.isRequired;
            });

            if (hasRequiredChildren) {
              return visibleChildren?.every((child) => {
                if (child.data?.validation?.isRequired) {
                  return !!child.answer?.value;
                }
                return true;
              });
            }
            return visibleChildren?.some((child) => !!child.answer?.value);
          }

          const questionState =
            questionnaireState.questions[question.compositeKey ?? question.id];

          // Group children are part of "All Questions" / "Visible Questions" so we need to skip them here
          if (questionState.skipForCount) {
            return false;
          }

          return questionState.answer;
        }).length,
        qcMark: countedQuestions.every((question) => {
          if (!question.isQcRequired) {
            return true;
          }
          const state =
            questionnaireState.questions[question.compositeKey ?? question.id];

          return state.qcMark;
        }),
        smeMark: countedQuestions.every((question) => {
          if (!question.isSmeRequired) {
            return true;
          }

          const state =
            questionnaireState.questions[question.compositeKey ?? question.id];

          return state.smeMark;
        }),
        questions: allQuestions.map((question) => {
          const state =
            questionnaireState.questions[question.compositeKey ?? question.id];

          return state;
        }),
      };

      formattedSubmodule.is_complete =
        formattedSubmodule.answered_questions ===
        formattedSubmodule.visible_questions;

      questionnaireState.modules[subModule.id] = formattedSubmodule;
    });

    // Finally derive visibility of the module
    const conditionGroups = createConditionGroups(module);

    const visible = evaluateConditionGroups(
      conditionGroups,
      questionnaireState,
    );

    const visibleSubmodules = module.subModules.filter((subModule) => {
      const state = questionnaireState.modules[subModule.id];

      return state.visible;
    });

    if (!visible) {
      // If the module is not visible, set all submodules and questions to not visible
      module.subModules.forEach((subModule) => {
        const state = questionnaireState.modules[subModule.id];

        questionnaireState.modules[subModule.id] = {
          ...state,
          visible: false,
        };

        subModule.questions.forEach((question) => {
          const questionState =
            questionnaireState.questions[question.compositeKey ?? question.id];

          questionnaireState.questions[question.compositeKey ?? question.id] = {
            ...questionState,
            visible: false,
          };
        });
      });
    }
    const visible_questions = visibleSubmodules.reduce((acc, subModule) => {
      return acc + questionnaireState.modules[subModule.id].visible_questions;
    }, 0);
    const answered_questions = visibleSubmodules.reduce(
      (acc, subModule) =>
        acc + questionnaireState.modules[subModule.id].answered_questions,
      0,
    );

    const formattedModule: DerivedModule = {
      id: module.id,
      title: module.title,
      conditionGroups,
      visible,
      visible_questions,
      answered_questions,
      qcMark: visibleSubmodules.every((subModule) => {
        const state = questionnaireState.modules[subModule.id];

        return state.qcMark;
      }),
      total_questions: module.subModules.reduce(
        (acc, subModule) => acc + subModule.questions.length,
        0,
      ),
      smeMark: visibleSubmodules.every((subModule) => {
        const state = questionnaireState.modules[subModule.id];

        return state.smeMark;
      }),
      questions: getAllModuleQuestions(module).map((question) => {
        const state =
          questionnaireState.questions[question.compositeKey ?? question.id];

        return state;
      }),
      is_complete: visible_questions === answered_questions,
    };

    questionnaireState.modules[module.id] = formattedModule;
  });

  return questionnaireState;
}

/**
 * Derive the visibility of the questions in the questionnaire
 * @param derivedQuestionnaireState The state of the questionnaire
 * @returns Whether the question is visible or not
 */
export function evaluateDerivedQuestionnaireState(
  derivedQuestionnaireState: DerivedQuestionnaireState,
): DerivedQuestionnaireState {
  const compositeKeys = Object.keys(derivedQuestionnaireState);

  compositeKeys.forEach((compositeKey) => {
    const questionState = derivedQuestionnaireState.questions[compositeKey];

    const visible = questionState.conditionGroups.every((group) =>
      evaluateConditionGroup(group, derivedQuestionnaireState),
    );

    derivedQuestionnaireState.questions[compositeKey] = {
      ...questionState,
      visible,
    };
  });

  return derivedQuestionnaireState;
}

/**
 * Evaluate a list of condition groups based on the answers to the questions
 * @param groups The condition groups to evaluate
 * @param questionnaireState The state of the questionnaire
 * @param operator The operator to use to evaluate the condition groups
 * @returns Whether the condition groups are true or false
 */
function evaluateConditionGroups(
  groups: ConditionGroup[],
  questionnaireState: DerivedQuestionnaireState,
  operator: ConditionalOperator = 'or',
) {
  if (!groups.length) {
    return true;
  } else if (operator === 'or') {
    return groups.some((group) =>
      evaluateConditionGroup(group, questionnaireState),
    );
  }
  return groups.every((group) =>
    evaluateConditionGroup(group, questionnaireState),
  );
}

/**
 * Evaluate a condition group based on the answers to the questions
 * @param group The condition group to evaluate
 * @param questionState The state of the questionnaire
 * @returns Whether the condition group is true or false
 */
function evaluateConditionGroup(
  group: ConditionGroup,
  questionState: DerivedQuestionnaireState,
): boolean {
  if (group.operator === 'or') {
    return group.conditions.some((condition) =>
      // @ts-expect-error may have edge cases, please check
      evaluateCondition(condition, questionState),
    );
  }
  return group.conditions.every((condition) =>
    // @ts-expect-error may have edge cases, please check
    evaluateCondition(condition, questionState),
  );
}

/**
 * Evaluate a condition based on the answer to a question
 * @param condition The condition to evaluate
 * @param questionState The state of the questionnaire
 * @returns Whether the condition is true or false
 */
function evaluateCondition(
  condition: CamelCaseCondition,
  questionState: DerivedQuestionnaireState,
): boolean {
  // Find the composite key for condition.questionId
  const compositeKey = Object.keys(questionState.questions).find(
    (key) => questionState.questions[key].id === condition.questionId,
  );

  if (!compositeKey) {
    console.warn(
      `Question with id ${condition.questionId} not found in questionnaire state; are the order numbers correct?`,
    );

    return false;
  }

  // This should be unreachable as long as the order numbers are in correctly.
  if (!questionState.questions[compositeKey]) {
    console.warn(
      `Question with id ${condition.questionId} not found in questionnaire state; are the order numbers correct?`,
    );

    return false;
  }

  const conditionalQuestion = questionState.questions[compositeKey];

  // If the conditional question is not answered, do not evaluate the condition
  if (
    !conditionalQuestion.answerId ||
    conditionalQuestion.answer === '' ||
    (Array.isArray(conditionalQuestion.answer) &&
      !conditionalQuestion.answer.length)
  ) {
    return false;
  }

  const conditionalQuestionModule =
    questionState.modules[conditionalQuestion.moduleId];

  const moduleParent =
    questionState.modules[conditionalQuestionModule?.parentId ?? ''];

  // If the question or its module is not visible, the condition is false
  if (
    !conditionalQuestion.visible ||
    (conditionalQuestionModule && !conditionalQuestionModule.visible) ||
    (moduleParent && !moduleParent.visible)
  ) {
    return false;
  }

  const { answer, type } = conditionalQuestion;
  const conditionValue = condition.value?.value ?? '';

  switch (condition.operator) {
    case '==':
      if (Array.isArray(answer)) {
        return answer.includes(conditionValue);
      } else if (type === 'date') {
        return (
          new Date(answer).getTime() === new Date(conditionValue).getTime()
        );
      } else if (typeof conditionValue === 'string') {
        return answer.toLowerCase() === conditionValue.toLowerCase();
      }
      return answer == conditionValue;

    case '!=':
      if (Array.isArray(answer)) {
        return !answer.includes(conditionValue);
      } else if (type === 'date') {
        return (
          !!answer &&
          new Date(answer).getTime() !== new Date(conditionValue).getTime()
        );
      } else if (typeof conditionValue === 'string') {
        return answer.toLowerCase() !== conditionValue.toLowerCase();
      }
      return answer != conditionValue;

    case '>':
      if (Array.isArray(answer)) {
        throw new Error('Cannot compare array with number');
      } else if (type === 'date') {
        return new Date(answer) > new Date(conditionValue);
      }

      return Number(answer) > Number(conditionValue);
    case '>=':
      if (Array.isArray(answer)) {
        throw new Error('Cannot compare array with number');
      } else if (type === 'date') {
        return new Date(answer) >= new Date(conditionValue);
      }

      return Number(answer) >= Number(conditionValue);
    case '<':
      if (Array.isArray(answer)) {
        throw new Error('Cannot compare array with number');
      } else if (type === 'date') {
        return new Date(answer) < new Date(conditionValue);
      }

      return Number(answer) < Number(conditionValue);
    case '<=':
      if (Array.isArray(answer)) {
        throw new Error('Cannot compare array with number');
      } else if (type === 'date') {
        return new Date(answer) <= new Date(conditionValue);
      }

      return Number(answer) <= Number(conditionValue);
    default:
      return false;
  }
}

/**
 * Create condition groups for a question, module, or sub-module based on the conditions
 * @param question The question, module, or sub-module to create condition groups for
 * @returns An array of condition groups
 */
export function createConditionGroups(
  question: IQuestion | IModule | SubModule,
): ConditionGroup[] {
  const conditionGroups: { [num: number]: ConditionGroup } = {};

  if (!question.conditions) {
    return [];
  }

  question.conditions.forEach((condition) => {
    const groupNum = condition.groupNum ?? 0;

    if (!conditionGroups[groupNum]) {
      conditionGroups[groupNum] = {
        number: condition.groupNum ?? 0,
        conditions: [],
        operator: condition.conditionOperator,
      };
    }

    // @ts-expect-error may have edge cases, please check
    conditionGroups[groupNum].conditions.push(condition);
  });

  return Object.values(conditionGroups);
}

/**
 * Convert a list of condition groups to a list of conditions
 * @param conditionGroups The condition groups to convert to a list of conditions
 * @returns A list of conditions
 */
export function conditionGroupToConditionList(
  conditionGroups: ConditionGroup[],
): Condition[] {
  return (
    conditionGroups.reduce(
      (acc, group) =>
        (acc ?? []).concat(
          group.conditions.map((condition) => ({
            ...condition,
            group_num: group.number,
            condition_operator: group.operator,
          })),
        ),
      [] as Condition[],
    ) ?? []
  );
}

/**
 * Get a flat list of all questions (children, duplicates, etc) in the questionnaire
 * @param questionnaire Questionnaire object
 * @returns List of all questions in the questionnaire
 */
export function getAllQuestionnaireQuestions(questionnaire: Questionnaire) {
  return flatten(questionnaire.modules.map(getAllModuleQuestions));
}

/**
 * Get a flat list of all questions (children, duplicates, etc) in a module
 * @param module Module object
 * @returns List of all questions in the module
 */
export function getAllModuleQuestions(module: IModule): Array<IQuestion> {
  return flatten(module.subModules?.map(getSubmoduleQuestions));
}

/**
 * Get a flat list of all questions (children, duplicates, etc) in a submodule
 * @param subModule Submodule object
 * @returns List of all questions in the submodule
 */
export function getSubmoduleQuestions(subModule: SubModule): Array<IQuestion> {
  return flatten(subModule.questions?.map(getAllQuestions) ?? []);
}

/**
 * Get a flat list of all questions (children, duplicates, etc) in a question
 * @param question Question object
 * @returns List of all questions in the question
 */
export function getAllQuestions(question: IQuestion): Array<IQuestion> {
  const questions: IQuestion[] = [question];

  if (question.children) {
    questions.push(...question.children);
  }

  if (question.duplicates) {
    questions.push(...flatten(question.duplicates.map(getAllQuestions)));
  }

  return questions;
}

/**
 * Derive the composite key for a question from its answer
 * @param answer Question answer
 * @returns Composite key for the answer
 */
export function createCompositeKeyFromAnswer(
  answer: IAnswer,
): string | undefined {
  if (answer.group_num === undefined && answer.groupNum === undefined) {
    return answer.question_id ?? answer.questionId;
  }

  return `${answer.question_id ?? answer.questionId}-${answer.group_num ?? answer.groupNum}-${answer.id ?? answer.id}`;
}

/**
 * Derive the composite key for a question
 * @param question Question object
 * @returns Composite key for the question
 */
export function createCompositeKey(question: IQuestion) {
  let key = question.id;

  if (!question.answer) {
    return key;
  }

  if (typeof question.answer === 'string') {
    key += `-${question.answer}`;
  } else {
    if (question.answer.groupNum) {
      key += `-${question.answer.groupNum}`;
    } else if (question.answer.group_num) {
      key += `-${question.answer.group_num}`;
    }

    if (question.answer.id) {
      key += `-${question.answer.id}`;
    }
  }

  return key;
}

/**
 * Update the answer in the questionnaire
 * @param state The state of the questionnaire
 * @param answer The answer to update
 * @param mark The mark to update
 * @returns The updated state of the questionnaire
 */
export function updateAnswerInState(
  state: QuestionnaireState,
  answer: IAnswer,
  mark: SMEQC | undefined = undefined,
) {
  const nextState = cloneDeep(state);
  const camelCasedAnswer = new UtilsService().convertKeysToCamelCase({
    ...answer,
  });

  if (mark) {
    if (nextState.savingAnswerMarks?.[answer.id as string]) {
      nextState.savingAnswerMarks[answer.id as string][mark] = false;
    }

    if (nextState.answerMarkErrors?.[answer.id as string]) {
      nextState.answerMarkErrors[answer.id as string][mark] = '';
    }
  }

  if (nextState.questionnaire && nextState.questionnaire.modules) {
    nextState.questionnaire.modules = nextState.questionnaire.modules.map(
      (module) => {
        let answeredQuestions = module.answeredQuestions;
        let isComplete = module.isComplete;

        module.subModules = module.subModules.map((subModule) => {
          subModule.questions = subModule.questions.map((question) => {
            if (question?.answer?.id === camelCasedAnswer.id) {
              const updatedQuestion = {
                ...question,
                answer: {
                  ...question.answer,
                  ...camelCasedAnswer,
                },
              };
              if (question.answer.unanswered === true) {
                answeredQuestions++;
              }
              return updatedQuestion;
            }

            if (
              question.id === camelCasedAnswer.questionId &&
              !camelCasedAnswer.groupNum
            ) {
              answeredQuestions++;
              return {
                ...question,
                answer: camelCasedAnswer,
              };
            }

            if (question.children) {
              question.children = question.children.map((child) => {
                if (child?.answer?.id === camelCasedAnswer.id) {
                  return {
                    ...child,
                    answer: {
                      ...child.answer,
                      ...camelCasedAnswer,
                    },
                  };
                }

                if (
                  child.id === camelCasedAnswer.questionId &&
                  !camelCasedAnswer.groupNum
                ) {
                  return {
                    ...child,
                    answer: camelCasedAnswer,
                  };
                }
                return child;
              });
            }

            if (question.duplicates) {
              question.duplicates = question.duplicates.map(
                (duplicate, duplicateIndex) => {
                  if (
                    duplicate?.answer?.id === answer.id &&
                    camelCasedAnswer.groupNum === duplicate?.answer?.groupNum
                  ) {
                    return {
                      ...duplicate,
                      answer: {
                        ...duplicate.answer,
                        ...camelCasedAnswer,
                      },
                    };
                  } else if (
                    camelCasedAnswer.questionId === question.id &&
                    camelCasedAnswer.groupNum - 1 === duplicateIndex
                  ) {
                    return {
                      ...question,
                      answer: {
                        ...camelCasedAnswer,
                      },
                    };
                  }

                  duplicate.children = duplicate.children?.map((child) => {
                    if (
                      child?.answer?.id === answer.id &&
                      camelCasedAnswer.groupNum === child?.answer?.groupNum
                    ) {
                      return {
                        ...child,
                        answer: {
                          ...child.answer,
                          ...camelCasedAnswer,
                        },
                      };
                    } else if (
                      child?.id === camelCasedAnswer.questionId &&
                      camelCasedAnswer.groupNum - 1 === duplicateIndex
                    ) {
                      return {
                        ...child,
                        answer: {
                          ...camelCasedAnswer,
                        },
                      };
                    }
                    return child;
                  });
                  return duplicate;
                },
              );
            }
            return question;
          });
          return subModule;
        });

        if (module.totalQuestions === answeredQuestions) {
          isComplete = true;
        }

        return {
          ...module,
          answeredQuestions,
          isComplete,
        };
      },
    );

    nextState.questionnaireState = deriveQuestionnaireState(
      nextState.questionnaire,
    );

    return nextState;
  }

  throw new Error('Invalid answer');
}

/**
 * Add a duplicate to a question in the questionnaire
 *
 * Warning: This function mutates the question object
 */
export function addDuplicateToQuestion(question: IQuestion) {
  const duplicate = duplicateQuestion(question);

  if (!question.duplicates) {
    question.duplicates = [];
  }

  question.duplicates.push(duplicate);

  return question;
}

/**
 * Duplicate a question in the questionnaire
 */
export function duplicateQuestion(question: IQuestion) {
  const duplicate = cloneDeep(question);

  delete duplicate.duplicates;

  duplicate.children?.forEach((child) => delete child.answer);

  return duplicate;
}

export function formatToDateString(value: string, injector: Injector): string {
  return runInInjectionContext(injector, () => {
    const _datePipe = injector.get(DatePipe);
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return _datePipe.transform(date, 'dd-MMM-yyyy') as string;
    }
    return value;
  });
}
export function handleDuplicates(
  question: IQuestion,
  duplicateKeys: string[],
  duplicates: {
    [groupNum: number]: {
      [questionId: string]: string;
    };
  },
): IQuestion {
  duplicateKeys.forEach((groupNum) => {
    const clone = cloneDeep(question);
    delete clone.duplicates;

    clone.children?.forEach((child) => {
      child.answer = duplicates[+groupNum][child.id];
    });

    if (!question.duplicates) {
      question.duplicates = [];
    }

    question.duplicates.push(clone);
  });

  return question;
}

export function handleQuestionChoices(ctx: {
  url: string;
  response: { data?: unknown[] };
  question: IQuestion;
  multiChoices: (val?: WcgMenuOption[]) => WcgMenuOption[];
  setMultiChoices: (val: WcgMenuOption[]) => void;
  setAnswer: (val: unknown) => void;
  initControls: (i: number) => void;
}): void {
  const {
    url,
    response,
    question,
    multiChoices,
    setMultiChoices,
    setAnswer,
    initControls,
  } = ctx;

  if (!response || !question) return;

  const isStringArray =
    Array.isArray(response) && typeof response[0] === 'string';
  const isDropdown = question?.type?.name?.toLowerCase() === 'dropdown';
  const isMultiDropdown =
    question?.type?.name?.toLowerCase() === 'choicemultipledropdown';
  const isCountryCodeEndpoint =
    question?.data?.endpoint?.includes('country_codes');

  if (Array.isArray(response)) {
    let finalChoices: WcgMenuOption[] = [];

    if (!isMultiDropdown) {
      if (isStringArray) {
        finalChoices = ['', ...response];
      } else {
        const emptyChoice = url.includes('suffixes')
          ? { id: '', label: 'None', value: 'None' }
          : { id: '', label: '', value: '' };
        finalChoices = [emptyChoice, ...response];
      }
    } else {
      finalChoices = response;
    }

    if (isStringArray) {
      setMultiChoices(finalChoices);
    } else {
      const transformed = finalChoices.map((value: unknown) => ({
        id:
          url.includes('suffixes') &&
          (value as { value?: string }).value === 'None'
            ? ''
            : (value as { value?: string; id?: string }).value ||
              (value as { value?: string; id?: string }).id,
        label: (value as { value?: string }).value || '',
        value: (value as { value?: string }).value || '',
      }));
      setMultiChoices(transformed);
      setAnswer(
        transformed.find(
          (choice: WcgMenuOption) => choice.id === question?.answer?.value,
        ),
      );
    }

    multiChoices().forEach((_: unknown, i: number) => initControls(i));
  } else if (response?.data && isDropdown && isCountryCodeEndpoint) {
    const countryChoices = response.data.map((value: unknown) => {
      const countryCodeVal = `${(value as { country?: string; name?: string }).country ? (value as { country?: string; name?: string }).country + ' ' : ''}${(value as { country?: string; name?: string }).name || ''}`;
      return { id: countryCodeVal, label: countryCodeVal };
    });

    setMultiChoices([{ id: '', label: 'Select One' }, ...countryChoices]);
    setAnswer(
      countryChoices.find(
        (choice: WcgMenuOption) => choice.id === question?.answer?.value,
      ),
    );
  } else if (response?.data) {
    setMultiChoices(response.data as WcgMenuOption[]);
    multiChoices().forEach((_: WcgMenuOption, i: number) => initControls(i));
  }
}
